import { useCallback, useEffect } from 'react';
import {
  DndContext,
  DragOverlay,
  closestCorners,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  type DragStartEvent,
  type DragEndEvent,
  type DragOverEvent,
} from '@dnd-kit/core';
import { sortableKeyboardCoordinates, arrayMove } from '@dnd-kit/sortable';
import { KanbanColumn } from '@/components/pipeline/kanban-column';
import { DealCard } from '@/components/pipeline/deal-card';
import { usePipelineStore, setMoveStageApi } from '@/stores/pipeline.store';
import { DEFAULT_PIPELINE_STAGES } from '@trinco/shared';
import { trpc } from '@/trpc';
import type { Deal } from '@trinco/shared';

export function KanbanBoard() {
  const { deals, draggedDealId, setDraggedDeal, moveOptimistic, commitMove, setDeals } =
    usePipelineStore();

  // Wire tRPC moveStage into the store so commitMove can persist cross-column moves
  const moveStage = (trpc as any).deal?.moveStage?.useMutation?.();
  const reorderInStage = (trpc as any).deal?.reorderInStage?.useMutation?.();

  useEffect(() => {
    if (moveStage?.mutateAsync) {
      setMoveStageApi((dealId: string, stage: string) =>
        moveStage.mutateAsync({ id: dealId, stage }),
      );
    }
  }, [moveStage?.mutateAsync]);

  const stages = DEFAULT_PIPELINE_STAGES.filter((s) => !s.isLost);

  // Build per-stage deal lists (with optimistic stage overrides for cross-column drags)
  const dealsByStage: Record<string, Deal[]> = {};
  for (const stage of stages) {
    dealsByStage[stage.slug] = [];
  }
  for (const deal of deals) {
    const optimisticStage = usePipelineStore.getState().optimisticMoves.get(deal.id);
    const stage = optimisticStage ?? deal.stage;
    if (dealsByStage[stage]) {
      dealsByStage[stage]!.push(deal);
    }
  }

  const allDeals = deals;
  const draggedDeal = allDeals.find((d) => d.id === draggedDealId);

  // Which stage is each deal currently in (accounting for optimistic moves)?
  const getDealStage = (dealId: string) => {
    const deal = allDeals.find((d) => d.id === dealId);
    return usePipelineStore.getState().optimisticMoves.get(dealId) ?? deal?.stage ?? null;
  };

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  const handleDragStart = useCallback(
    (event: DragStartEvent) => {
      setDraggedDeal(event.active.id as string);
    },
    [setDraggedDeal],
  );

  // Live feedback as a card is dragged over a column
  const handleDragOver = useCallback(
    (event: DragOverEvent) => {
      const { active, over } = event;
      if (!over) return;

      const dealId = active.id as string;
      const overId = over.id as string;

      const currentStage = getDealStage(dealId);

      // over.id is either a column slug or another deal's id
      const isOverColumn = stages.some((s) => s.slug === overId);
      const targetStage = isOverColumn
        ? overId
        : getDealStage(overId) ?? currentStage;

      if (targetStage && targetStage !== currentStage) {
        moveOptimistic(dealId, targetStage);
      }
    },
    [allDeals, stages, moveOptimistic],
  );

  const handleDragEnd = useCallback(
    (event: DragEndEvent) => {
      const { active, over } = event;
      setDraggedDeal(null);

      if (!over) {
        // Dropped outside — clear optimistic
        usePipelineStore.getState().clearOptimistic(active.id as string);
        return;
      }

      const dealId = active.id as string;
      const overId = over.id as string;

      const deal = allDeals.find((d) => d.id === dealId);
      if (!deal) return;

      const fromStage = deal.stage;
      const isOverColumn = stages.some((s) => s.slug === overId);
      const toStage = isOverColumn ? overId : (getDealStage(overId) ?? fromStage);

      if (toStage !== fromStage) {
        // Cross-column move — commit stage change
        commitMove(dealId, toStage);
        return;
      }

      // Same-column reorder
      if (!isOverColumn && overId !== dealId) {
        const stageDeals = dealsByStage[fromStage] ?? [];
        const oldIndex = stageDeals.findIndex((d) => d.id === dealId);
        const newIndex = stageDeals.findIndex((d) => d.id === overId);

        if (oldIndex !== -1 && newIndex !== -1) {
          const reordered = arrayMove(stageDeals, oldIndex, newIndex);

          // Optimistically update deal order in store
          setDeals(
            allDeals.map((d) => {
              const idx = reordered.findIndex((r) => r.id === d.id);
              return idx !== -1 ? { ...d, position: (idx + 1) * 1000 } : d;
            }),
          );

          // Persist to API
          const afterId = newIndex > 0 ? reordered[newIndex - 1]!.id : null;
          const beforeId = newIndex < reordered.length - 1 ? reordered[newIndex + 1]!.id : null;
          reorderInStage?.mutate?.({ id: dealId, afterId, beforeId });
        }
      }
    },
    [allDeals, stages, dealsByStage, commitMove, setDeals, reorderInStage, setDraggedDeal],
  );

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCorners}
      onDragStart={handleDragStart}
      onDragOver={handleDragOver}
      onDragEnd={handleDragEnd}
    >
      <div className="flex gap-4 overflow-x-auto pb-4">
        {stages.map((stage) => (
          <KanbanColumn
            key={stage.id}
            stage={stage}
            deals={dealsByStage[stage.slug] ?? []}
          />
        ))}
      </div>

      <DragOverlay>
        {draggedDeal ? <DealCard deal={draggedDeal} isDragOverlay /> : null}
      </DragOverlay>
    </DndContext>
  );
}
